import React, { useEffect, useState } from 'react';
import { Time } from 'lightweight-charts';
import { Card, Loading, Heading } from './core';
import { getCustomVaultName, useVaultPerformance } from '@sc1/common';
import { formatDollars, formatPercentage } from '@sc1/common/utils';
import { useChain } from '@sc1/common/hooks';
import { useThemedChart, useSeriesEvents } from '../hooks';
import { useParams } from 'react-router-dom';
import { createChart } from '../utils';
import { Graph, GraphEvents } from './Graph';

export const VaultPerformance = (): JSX.Element => {
  // params
  const { vaultAddress } = useParams();
  const chain = useChain();

  // query for chart data
  const { loading, data } = useVaultPerformance(chain, vaultAddress) || {};
  const { dataPoints } = data || {};
  const customName = getCustomVaultName(dataPoints?.[0]?.vault?.name);
  const chartId = `${vaultAddress}-${chain}-performance`;

  const [[tvl, pnl], setLegends] = useState<[number, number] | [null, null]>([
    0, 0,
  ]);

  const { events, markers } = useSeriesEvents(chain, vaultAddress);
  const chartStyles = useThemedChart();

  const crosshairMoveHandler = (params, series) => {
    // updates legends
    const { seriesPrices, time } = params;
    if (!time) setLegends([null, null]);
    else setLegends(series.map((serie) => seriesPrices.get(serie)));
  };

  useEffect(() => {
    // create graph
    if (!dataPoints?.length) return;
    const chart = createChart(chartId, chartStyles);
    chart.subscribeCrosshairMove((params) => {
      crosshairMoveHandler(params, [tvlLineSeries, pnlLineSeries]);
    });
    const tvlLineSeries = chart.addAreaSeries({
      priceScaleId: 'left',
      lineWidth: 2,
      priceFormat: { type: 'volume' },
    });
    const tvlData = dataPoints.map((d) => ({
      value: Number(d.tvl) / Math.pow(10, d.vault.decimals),
      time: Number(d.timestamp) as Time,
    }));
    tvlLineSeries.setData(tvlData);
    const pnlLineSeries = chart.addLineSeries({
      lineWidth: 2,
      priceFormat: {
        type: 'custom',
        formatter: (x) => formatPercentage(x / 100),
      },
    });
    const pnlData = dataPoints.map((d) => ({
      value:
        (Number(d.pricePerShare) / Math.pow(10, d.vault.decimals) - 1) * 100,
      time: Number(d.timestamp) as Time,
    }));
    pnlLineSeries.setData(pnlData);
    setLegends([
      tvlData[tvlData.length - 1].value,
      pnlData[pnlData.length - 1].value,
    ]);
    chart.timeScale().fitContent();
    if (markers) pnlLineSeries.setMarkers(markers);
    return () => {
      // unsubscribe on unmount
      chart.unsubscribeCrosshairMove((params) => {
        crosshairMoveHandler(params, [tvlLineSeries, pnlLineSeries]);
      });
    };
  }, [dataPoints?.length]);

  return (
    <Card>
      <Loading loading={loading}>
        {/* additional spinner for loading vault customName */}
        <Heading mb={4}>{customName} Historical Performance</Heading>
      </Loading>
      <Graph
        chartId={chartId}
        loading={loading}
        legends={[
          {
            label: 'Total Value Locked',
            value: tvl ? formatDollars(tvl) : '',
            color: 'green',
          },
          {
            label: 'Profit/Loss',
            value: pnl ? formatPercentage(pnl / 100) : '',
            color: 'lightBlue',
          },
        ]}
      />
      <GraphEvents events={events} />
    </Card>
  );
};
